import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";

const APP_URL = "https://schema-analysis-hub.emergent.host";

const links = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/for-councils", label: "For Councils", id: "nav-councils-link" },
  { to: "/for-consultants", label: "For Consultants", id: "nav-consultants-link" },
  { to: "/#how-it-works", label: "How It Works", id: "nav-workflow-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-zinc-200" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2 group">
          <span className="h-8 w-8 grid place-items-center bg-[#0033A0] text-white">
            <LogoMark size={18} />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight text-zinc-900">
            WebADAC
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.to.includes("#") ? (
              <a
                key={l.id}
                href={l.to}
                data-testid={l.id}
                className="text-sm font-medium text-zinc-600 hover:text-[#0033A0] transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-[#0033A0]" : "text-zinc-600 hover:text-[#0033A0]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="/#pricing"
            data-testid="nav-pricing-link"
            className="text-sm font-medium text-zinc-700 hover:text-[#0033A0] transition-colors"
          >
            See Pricing
          </a>
          <a
            href={APP_URL}
            data-testid="nav-existing-users-link"
            className="text-sm font-medium text-zinc-900 border border-zinc-300 hover:bg-zinc-50 transition-colors px-4 py-2 rounded-sm"
          >
            Existing users
          </a>
          <button
            data-testid="nav-book-demo-button"
            onClick={() => navigate("/contact")}
            className="bg-[#0033A0] text-white hover:bg-[#00257A] transition-colors px-5 py-2.5 text-sm font-medium rounded-sm"
          >
            Book a demo
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-zinc-800"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-b border-zinc-200 px-6 py-4 space-y-3">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.to}
              onClick={() => setOpen(false)}
              className="block text-sm font-medium text-zinc-700"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/#pricing"
            data-testid="mobile-pricing-link"
            className="block text-sm font-medium text-zinc-700"
          >
            See Pricing
          </a>
          <a
            href={APP_URL}
            data-testid="mobile-existing-users-link"
            className="block text-sm font-medium text-zinc-900"
          >
            Existing users
          </a>
          <button
            data-testid="mobile-book-demo-button"
            onClick={() => {
              setOpen(false);
              navigate("/contact");
            }}
            className="w-full bg-[#0033A0] text-white px-5 py-2.5 text-sm font-medium rounded-sm"
          >
            Book a demo
          </button>
        </div>
      )}
    </header>
  );
}
